import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logError, logDatabaseOperation, logApiCall, addBreadcrumb } from "@/lib/logger";
import { Message } from "@/types/message";

interface UseAIChatProps {
  userId: string | null;
  messages: Message[];
  checkIfAtBottom: () => boolean;
  setHasNewMessages: (value: boolean) => void;
  setEditingMessageIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setWorkInProgressIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const useAIChat = ({
  userId,
  messages,
  checkIfAtBottom,
  setHasNewMessages,
  setEditingMessageIds,
  setWorkInProgressIds,
}: UseAIChatProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showThinking, setShowThinking] = useState(false);
  const thinkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const sendMessageAndGetAIResponse = async (
    userContent: string,
    options?: { originalMessageId?: string }
  ) => {
    if (!userId) {
      toast.error("Please sign in to send messages.");
      return false;
    }
    
    const currentUserId = userId;
    
    addBreadcrumb('Sending message', { 
      user_id: currentUserId, 
      content_length: userContent.length,
      is_edit: !!options?.originalMessageId 
    }, 'info', 'chat');
    
    setIsLoading(true);
    
    thinkingTimeoutRef.current = setTimeout(() => {
      setShowThinking(true);
    }, 1000);
    
    try {
      const startInsert = performance.now();
      const { data: userMessage, error: userError } = await supabase
        .from("messages")
        .insert({
          user_id: currentUserId,
          role: "user",
          content: userContent,
          editor_status: "closed",
        })
        .select()
        .single();

      if (userError) {
        logDatabaseOperation('insert', 'messages', false, userError, { 
          user_id: currentUserId,
          role: 'user' 
        });
        throw userError;
      }

      logDatabaseOperation('insert', 'messages', true, undefined, { 
        user_id: currentUserId,
        role: 'user',
        message_id: userMessage.id,
        duration_ms: (performance.now() - startInsert).toFixed(2)
      });

      const formattedMessages = [...messages, userMessage].map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      addBreadcrumb('Calling AI chat function', { 
        message_count: formattedMessages.length 
      }, 'info', 'ai');
      
      const startAI = performance.now();
      const { data, error } = await supabase.functions.invoke("chat", {
        body: { 
          userId: currentUserId,
          messages: formattedMessages
        }
      });
      const aiDuration = performance.now() - startAI;

      if (error) {
        logApiCall('chat', 'POST', error.status || 500, aiDuration, error, {
          user_id: currentUserId,
          message_count: formattedMessages.length
        });
        throw error;
      }

      if (!data?.response) {
        const noResponseError = new Error("No response from AI");
        logApiCall('chat', 'POST', 200, aiDuration, noResponseError, {
          user_id: currentUserId,
          has_data: !!data
        });
        throw noResponseError;
      }

      logApiCall('chat', 'POST', 200, aiDuration, undefined, {
        user_id: currentUserId,
        response_length: data.response.length
      });

      await supabase
        .from("messages")
        .update({ is_latest_ai: false })
        .eq("user_id", userId)
        .eq("role", "assistant");

      console.log('[AI_RESPONSE] Inserting AI message');
      const { error: insertError } = await supabase
        .from("messages")
        .insert({
          user_id: userId,
          role: "assistant",
          content: data.response,
          is_latest_ai: true,
          editor_status: "closed",
        });

      if (insertError) {
        logDatabaseOperation('insert', 'messages', false, insertError, { 
          user_id: userId,
          role: 'assistant' 
        });
      } else {
        logDatabaseOperation('insert', 'messages', true, undefined, { 
          user_id: userId,
          role: 'assistant' 
        });
      }

      const atBottom = checkIfAtBottom();
      console.log('[AI_RESPONSE] After insert, atBottom:', atBottom);
      
      if (atBottom) {
        console.log('[AI_RESPONSE] User at bottom, will auto-scroll via realtime');
      } else {
        console.log('[AI_RESPONSE] User NOT at bottom, showing button');
        setHasNewMessages(true);
      }

      if (options?.originalMessageId) {
        await supabase
          .from("messages")
          .update({ editor_status: "closed" })
          .eq("id", options.originalMessageId);

        setEditingMessageIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(options.originalMessageId);
          return newSet;
        });
        
        setWorkInProgressIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(options.originalMessageId);
          return newSet;
        });
      }

      addBreadcrumb('Message sent successfully', { 
        user_id: userId,
        total_duration_ms: (performance.now() - startInsert).toFixed(2)
      }, 'info', 'chat');

      return true;
    } catch (error: any) {
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: 'send_message',
        user_id: userId,
        content_length: userContent.length,
        is_edit: !!options?.originalMessageId
      });
      toast.error(error.message || "Failed to send message");
      return false;
    } finally {
      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current);
        thinkingTimeoutRef.current = null;
      }
      setShowThinking(false);
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    showThinking,
    sendMessageAndGetAIResponse,
  };
};
